import React, { useState, useEffect } from "react";
import Modal from "../Modal";
import { getReceipt, getPharmacyBillHtml, getOpdBillHtml, sendOpdReceiptWhatsApp } from "../../apis/receipts";
import { getInvoiceBill } from "../../apis/completedPayments";
import OpdReceipt from "./OpdReceipt";
import PathologyReceipt from "./PathologyReceipt";
import IpdReceipt from "./IpdReceipt";
import PharmacyBill from "./PharmacyBill";
import ReceiptSlipModal, { ReceiptSlipData } from "./ReceiptSlipModal";
import TransactionsModal from "./TransactionsModal";

type ReceiptType = "opd" | "ipd" | "pathology" | "pharmacy";

type Transaction = {
  allocation_id: number;
  transaction_id: number;
  payment_date: string | null;
  payment_mode: string;
  amount: number;
  receipt_number: string | null;
};

type ReceiptModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
  receiptType: ReceiptType;
};

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  receiptType,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waResult, setWaResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedReceiptData, setSelectedReceiptData] = useState<ReceiptSlipData | null>(null);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setData(null);
      setError(null);
      setInvoice(null);
      setTransactions([]);
      setShowTransactionsModal(false);
      setSelectedReceiptData(null);
      setWaResult(null);
      return;
    }

    setLoading(true);
    setError(null);
    getReceipt({ invoiceId, type: receiptType })
      .then((res: any) => {
        const result = res?.data ?? res;
        setData(result);
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          err?.message ??
          "Failed to load receipt";
        setError(msg);
      })
      .finally(() => setLoading(false));

    setTransactionsLoading(true);
    getInvoiceBill(invoiceId)
      .then((res: any) => {
        const result = res?.data ?? res;
        setInvoice(result?.invoice ?? null);
        setTransactions(result?.transactions ?? []);
      })
      .catch(() => setTransactions([]))
      .finally(() => setTransactionsLoading(false));
  }, [isOpen, invoiceId, receiptType]);

  const handlePrint = () => {
    if (receiptType === "opd" || receiptType === "pharmacy") {
      if (!invoiceId) return;
      setPdfLoading(true);
      const htmlApi = receiptType === "opd"
        ? () => getOpdBillHtml({ invoiceId: invoiceId! })
        : () => getPharmacyBillHtml({ invoiceId: invoiceId! });
      htmlApi()
        .then((res: any) => {
          const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
          window.open(blobUrl, "_blank");
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch((err: any) => {
          const msg =
            err?.response?.data?.error ??
            err?.response?.data?.message ??
            err?.message ??
            "Failed to generate bill";
          setError(msg);
        })
        .finally(() => setPdfLoading(false));
    } else {
      window.print();
    }
  };

  const handleSendWhatsApp = () => {
    if (!invoiceId) return;
    setWaSending(true);
    setWaResult(null);
    sendOpdReceiptWhatsApp(invoiceId)
      .then(() => setWaResult({ ok: true, msg: "Receipt sent on WhatsApp!" }))
      .catch((err: any) => {
        const msg = err?.response?.data?.message ?? "Failed to send receipt on WhatsApp";
        setWaResult({ ok: false, msg });
      })
      .finally(() => setWaSending(false));
  };

  const isPharmacy = receiptType === "pharmacy";

  const hasSingleTransaction = transactions.length === 1;
  const hasMultipleTransactions = transactions.length > 1;

  if (selectedReceiptData) {
    return (
      <ReceiptSlipModal
        isOpen={true}
        onClose={() => setSelectedReceiptData(null)}
        receiptData={selectedReceiptData}
      />
    );
  }

  if (showTransactionsModal && invoiceId) {
    return (
      <TransactionsModal
        isOpen={true}
        onClose={() => setShowTransactionsModal(false)}
        invoiceId={invoiceId}
      />
    );
  }

  return (
    <Modal
      title="View Bill"
      onClose={onClose}
      width={isPharmacy ? "sm:max-w-6xl" : "sm:max-w-4xl"}
      modalContent={
        <div className="receipt-modal-content">
          {loading && (
            <div className="py-12 text-center text-gray-500">
              Loading receipt...
            </div>
          )}
          {error && (
            <div className="py-8 text-center text-red-600">{error}</div>
          )}
          {isPharmacy && !error && data && (
            <>
              <div className="p-4 bg-white receipt-print-area pharmacy-bill-print-area">
                <PharmacyBill data={data} />
              </div>
              <div className="flex flex-wrap gap-2 justify-end mt-4 print:hidden">
                {!transactionsLoading && hasSingleTransaction && invoice && (
                  <button
                    type="button"
                    onClick={() => {
                      const tx = transactions[0];
                      setSelectedReceiptData({
                        patientName: invoice.patientName ?? "",
                        receiptNumber: (tx as any).receiptNumber ?? (tx as any).receipt_number ?? "",
                        invoiceNumber: invoice.invoiceNumber ?? "",
                        paymentDate: (tx as any).paymentDate ?? (tx as any).payment_date,
                        paymentMode: (tx as any).paymentMode ?? (tx as any).payment_mode ?? "",
                        amount: tx.amount,
                        clinicName: invoice.clinicName,
                        notes: (tx as any).notes,
                      });
                    }}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Receipt
                  </button>
                )}
                {!transactionsLoading && hasMultipleTransactions && (
                  <button
                    type="button"
                    onClick={() => setShowTransactionsModal(true)}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Transactions
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={!invoiceId || pdfLoading}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pdfLoading ? "Generating..." : "Print"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </>
          )}
          {!loading && !error && data && !isPharmacy && (
            <>
              <div className="p-4 bg-white receipt-print-area">
                {data.receiptType === "opd" && <OpdReceipt data={data} />}
                {data.receiptType === "ipd" && <IpdReceipt data={data} />}
                {data.receiptType === "pathology" && (
                  <PathologyReceipt data={data} />
                )}
              </div>
              {waResult && (
                <div className={`mx-4 mb-1 px-3 py-2 rounded-md text-sm ${waResult.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                  {waResult.msg}
                </div>
              )}
              <div className="flex flex-wrap gap-2 justify-end mt-4 print:hidden">
                {!transactionsLoading && hasSingleTransaction && invoice && (
                  <button
                    type="button"
                    onClick={() => {
                      const tx = transactions[0];
                      setSelectedReceiptData({
                        patientName: invoice.patientName ?? "",
                        receiptNumber: (tx as any).receiptNumber ?? (tx as any).receipt_number ?? "",
                        invoiceNumber: invoice.invoiceNumber ?? "",
                        paymentDate: (tx as any).paymentDate ?? (tx as any).payment_date,
                        paymentMode: (tx as any).paymentMode ?? (tx as any).payment_mode ?? "",
                        amount: tx.amount,
                        clinicName: invoice.clinicName,
                        notes: (tx as any).notes,
                      });
                    }}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Receipt
                  </button>
                )}
                {!transactionsLoading && hasMultipleTransactions && (
                  <button
                    type="button"
                    onClick={() => setShowTransactionsModal(true)}
                    className="px-4 py-2 text-sm font-semibold rounded-md border text-primary border-primary hover:bg-primary/5"
                  >
                    View Transactions
                  </button>
                )}
                {receiptType === "opd" && (
                  <button
                    type="button"
                    onClick={handleSendWhatsApp}
                    disabled={waSending}
                    className="px-4 py-2 text-sm font-semibold text-[#25D366] rounded-md border border-[#25D366] hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {waSending ? "Sending..." : "Send on WhatsApp"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={receiptType === "opd" && pdfLoading}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {receiptType === "opd" && pdfLoading ? "Generating..." : "Print"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

export default ReceiptModal;
